import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MeasuredDemandVM, ResourceSeriesVM, SiteMicVM, SolarConfigVM, TariffRateVM } from '@models/backend-generated-models/models';
import { MicService } from '@api/mic.service';
import { TariffService } from '@api/tariff.service';
import { BatteryHour, BatteryResult, DEFAULT_NIGHT_FROM_HOUR, DEFAULT_NIGHT_TO_HOUR, PeakShaveResult, TariffBand, nightHours, priceBattery, scalePower, shavePeak, simulateBattery } from './battery-sim';
import { ChartGuide } from './chart-guides';
import { SeriesBand } from './series-bands';
import { bucketWindowDays, darken, isGenerationName, lighten, parseRgb, pickMains } from './series-util';
import { ResourceTotals } from './resource-series-chart.component';

/** What the panel hands back whenever the scenario changes. */
export interface WhatIfScenario {
  /**
   * The series the chart should draw, already scaled and clipped.
   *
   * Null means "nothing has been changed, draw what you loaded", which is not the same as handing
   * back a copy of the response: it tells the chart it can skip work rather than redo it.
   */
  series: ResourceSeriesVM[] | null;

  /** Bands to overlay, per the contract in series-bands.ts. Empty when no battery is modelled. */
  bands: SeriesBand[];

  /**
   * The generation multiplier and the interval cap that produced those series.
   *
   * The chart needs both ONLY for its tiles, which recompute the gross import/export split from
   * hourly rows and would otherwise disagree with the bars they sit above. When that split moves to
   * the server these two go with it and the scenario narrows to series plus bands.
   */
  multiplier: number;
  /** kWp of estimated solar on a site with none metered, so the tiles can count it. Zero otherwise. */
  addedKwp: number;
  cap: number | null;

  /** How to describe the change on the tiles, e.g. "Solar PV +300 kWp". Null when nothing changed. */
  note: string | null;

  /**
   * The exact rawSeries this scenario was worked out from, by reference. The chart only draws a
   * scenario while this is still the series it is showing: a drill swaps the series, and a scenario
   * for the level just left, series and bands both, must never be drawn over the new one.
   */
  basis: ResourceSeriesVM[];

  /** Lines and shaded hours to mark on the chart, per the contract in chart-guides.ts. */
  guides: ChartGuide[];
}

/**
 * The what-if: solar sizing, batteries, tariffs, connection size and every euro figure.
 *
 * Split out of the chart on 22/09/26. The chart draws named, coloured, ordered series and knows
 * nothing about what any of them mean; this knows what they mean and nothing about how they are
 * drawn. Only two things cross between them, and they are both in WhatIfScenario: the series to
 * draw, and the bands to overlay.
 *
 * It still takes hourly readings as an INPUT rather than fetching them, because the chart's tiles
 * need the same rows to split gross import from gross export. That split is moving to the server,
 * and when it lands the fetch comes in here and the chart never makes an eight second request
 * again.
 */
@Component({
  selector: 'app-what-if-panel',
  templateUrl: './what-if-panel.component.html',
  styleUrls: ['./what-if-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhatIfPanelComponent implements OnChanges {

  // ---- What comes in ----------------------------------------------------------------------------

  /** The response's own series, never mutated: every multiplier is applied to these. */
  @Input() rawSeries: ResourceSeriesVM[] = [];

  /** The site's recorded solar, battery and tariff figures, null where nobody has filled them in. */
  @Input() solarConfig: SolarConfigVM | null = null;

  /**
   * The rates the client saved for this site in the Tariffs fold. Empty where none are saved, and
   * then the back-end rates in solarConfig price everything, exactly as before.
   */
  @Input() tariffRates: TariffRateVM[] = [];

  /** The site the rates are saved against. */
  @Input() siteId: number | null = null;

  /** The MIC the client entered here, the site's newest entry. Null falls back to DCS's figure. */
  @Input() siteMic: SiteMicVM | null = null;

  /**
   * Where the estimated solar on a site with no solar meter comes from, in the server's words: the EU's
   * PVGIS model for the site's location, or the middle of Ireland where none is set, or why there is
   * no estimate.
   */
  @Input() solarEstimateNote: string | null = null;

  /** kWh one kWp makes on each day of a typical year at the site, from PVGIS. See seasonFactor. */
  @Input() solarYearByDay: number[] | null = null;

  /** Measured peak demand, for the connection size panel. */
  @Input() measured: MeasuredDemandVM | null = null;

  /** Hourly readings for the whole window. A battery cannot be modelled on anything coarser. */
  @Input() hourlyRows: BatteryHour[] = [];
  @Input() hourlyLoaded = false;

  /** The chart's own buckets, so band values line up with the bars they belong to. */
  @Input() bucketDates: Date[] = [];

  /**
   * Exclusive end of the displayed window.
   *
   * Stops the last bucket collecting every traced hour after it. Used to come off the chart's drill
   * stack, which meant this component had to know what drilling was; it does not, and now it is one
   * date.
   */
  @Input() bucketWindowEnd: Date | null = null;

  /** Totals off the drawn series, and off the unscaled ones. Both computed by the chart's tiles. */
  @Input() totals: ResourceTotals | null = null;
  @Input() baseTotals: ResourceTotals | null = null;

  @Input() unit = '';
  @Input() intervalCap?: number;
  @Input() gridLogicalGroupId?: number;

  /** Which groups can be scaled, named from the response. */
  @Input() scaleOptions: { id: number | null; name: string }[] = [];
  @Input() scaleGroupId: number | null = null;

  /**
   * Pick the group to scale: whichever is NOT the grid.
   *
   * Scaling the grid is never what anyone means by a solar what-if, and with nothing chosen the
   * multiplier matches no series and silently does nothing at all. That is exactly what happened
   * when this moved out of the chart and nobody re-homed it: the panel looked alive, every figure
   * in it moved, and the chart never changed.
   */
  private defaultScaleGroupId(series: ResourceSeriesVM[]): number | null {
    const grid = this.grid(series);
    const other = series.find(s => s !== grid) ?? series[0];
    return other?.logicalGroupId ?? null;
  }

  // ---- What goes out ----------------------------------------------------------------------------

  /** The series to draw and the bands to overlay. The whole outward surface of this component. */
  @Output() scenarioChange = new EventEmitter<WhatIfScenario>();

  /** The panel asking to be closed. The header button elsewhere owns that state. */
  @Output() closed = new EventEmitter<void>();

  constructor(private cdr: ChangeDetectorRef, private tariffService: TariffService, private micService: MicService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // A new response means a new set of groups, so the choice of what to scale is remade. Only on a
    // genuinely new response: re-picking it on every recompute would fight the user's own choice.
    if ('rawSeries' in changes && this.rawSeries.length) {
      const stillThere = this.rawSeries.some(s => (s.logicalGroupId ?? null) === this.scaleGroupId);
      if (!stillThere) this.scaleGroupId = this.defaultScaleGroupId(this.rawSeries);
    }

    // `totals` is deliberately not a trigger for recompute(). It arrives back AFTER a scenario is
    // emitted, so recomputing on it would emit again and go round for ever.
    // The editable rows follow whatever the site has, until somebody starts changing them.
    if ('tariffRates' in changes || 'solarConfig' in changes) this.rateRows = this.rowsFromSite();
    if ('siteMic' in changes || 'solarConfig' in changes) this.micEntry = this.micKva;

    const triggers = ['rawSeries', 'solarConfig', 'tariffRates', 'hourlyRows', 'hourlyLoaded', 'bucketDates', 'intervalCap', 'solarYearByDay'];
    if (triggers.some(k => k in changes)) this.recompute();

    // But the saving is priced FROM those totals, and recompute() prices it before they have
    // arrived, so on its own every saving and payback belonged to the change before (found
    // 24/09/26: +100 kWp read â‚¬0, then +200's â‚¬19,128 on the way back). So re-price when they land,
    // and only re-price: nothing here emits.
    if ('totals' in changes || 'baseTotals' in changes) {
      this.deriveWhatItMeans(Number(this.multiplier) || 0);
      this.cdr.markForCheck();
    }
  }

  /** Close from inside the panel. The header button elsewhere owns the state. */
  closeWhatIf(): void {
    this.closed.emit();
  }

  /** The grid series, resolved the same way the chart resolves it. */
  private grid(series: ResourceSeriesVM[]): ResourceSeriesVM | undefined {
    return pickMains(series, this.gridLogicalGroupId);
  }

  /** 1 = as metered. The slider never mutates rawSeries. */
  multiplier = 1;

  /**
   * Whether the change can be sized in kWp rather than as a multiple of what was metered.
   *
   * Nobody is handed a proposal in multiples. Lee Strand's is "300 kWp added", every quote is
   * written in kWp, and asking for 2.2x makes the client do a division we are already holding both
   * numbers for. So kWp is what the panel asks for wherever the site's installed capacity is
   * recorded, and the multiple is derived and shown back rather than typed.
   *
   * The multiplier stays the model underneath: the whole what-if works by scaling a metered series,
   * and there is no other honest way to turn "300 kWp more" into half-hourly generation.
   */
  get canSizeByKwp(): boolean {
    return this.newArray || Number(this.solarConfig?.installedKwp) > 0;
  }

  /**
   * True where the chart has no solar meter, so any solar is one the client is thinking of adding.
   *
   * Jayden, 08/10/26: on a mains with no solar the client types a kWp and sees what it would
   * generate against their real consumption. The generation comes from the estimate the server sends
   * with the hourly rows (BatteryHour.kwpKwh): what one kWp makes each hour at the site, from PVGIS.
   *
   * The multiplier stays the one control underneath. Here it is read against one virtual kWp, so
   * multiplier - 1 is the kWp being tried and 1 is still "nothing changed".
   */
  get newArray(): boolean {
    return !this.rawSeries.some(s => isGenerationName(s.name)) && !!this.grid(this.rawSeries);
  }

  /** kWp of estimated solar being tried on a site with none metered. Zero everywhere else. */
  get estimatedKwp(): number {
    return this.newArray ? Math.max(0, (Number(this.multiplier) || 1) - 1) : 0;
  }

  /** What one step of the multiplier is worth in kWp: the array on record, or one virtual kWp. */
  private get sizingKwp(): number {
    if (this.newArray) return 1;
    const installed = Number(this.solarConfig?.installedKwp);
    return installed > 0 ? installed : 0;
  }

  /**
   * Capacity to add, kWp. Rounded to 0.1 so dragging the slider cannot write 313.6499999 into the
   * box, and so typing a figure back gives the same figure.
   */
  get addKwp(): number | null {
    const installed = this.sizingKwp;
    if (!(installed > 0)) return null;
    return Math.round((this.multiplier - 1) * installed * 10) / 10;
  }

  set addKwp(value: number | null) {
    const installed = this.sizingKwp;
    if (!(installed > 0)) return;
    // Floored at nothing added: "what if we take panels off" is not a question anyone is asking,
    // and a negative kWp box reads as a typo. The plain multiplier still goes below 1.
    const add = Math.max(0, Number(value) || 0);
    this.multiplier = 1 + add / installed;
  }

  /**
   * What is already on the roof, in the same three figures the "after" column answers in.
   *
   * Shown before anything is typed, because the first question anyone asks of a solar what-if is
   * "what have we got now". Panel count is derived rather than stored: installed kWp over the panel
   * rating is the same arithmetic the quote was built from, and at Lee Strand it lands on 408,
   * which is the as-built count.
   */
  get installedPanels(): number | null {
    const installed = Number(this.solarConfig?.installedKwp);
    const watts = Number(this.solarConfig?.panelWatts);
    if (!(installed > 0) || !(watts > 0)) return null;
    return Math.round((installed * 1000) / watts);
  }

  get installedAreaM2(): number | null {
    const area = Number(this.solarConfig?.panelAreaM2);
    const panels = this.installedPanels;
    return panels !== null && area > 0 ? panels * area : null;
  }

  get totalPanels(): number | null {
    const panels = this.installedPanels;
    if (panels === null) return null;
    return panels + (this.extraPanels ?? 0);
  }

  get totalAreaM2(): number | null {
    const area = Number(this.solarConfig?.panelAreaM2);
    const panels = this.totalPanels;
    return panels !== null && area > 0 ? panels * area : null;
  }

  /** Whether there is anything worth drawing in the array summary at all. */
  get showArraySpec(): boolean {
    return !this.newArray && Number(this.solarConfig?.installedKwp) > 0;
  }

  /** What the site would end up with, so the box can be read back as an array size. */
  get totalKwp(): number | null {
    if (this.newArray) return this.estimatedKwp;
    const installed = Number(this.solarConfig?.installedKwp);
    if (!(installed > 0)) return null;
    return installed * this.multiplier;
  }

  /** Slider ceiling: double what is there now, which covers any expansion anyone has proposed. */
  get maxAddKwp(): number {
    // A new array: up to twice the site's busiest hour, which is more than any roof it has would
    // carry, rounded to the slider's own step.
    if (this.newArray) {
      const busiest = this.hourlyRows.reduce((m, h) => Math.max(m, h.gridKwh + h.solarKwh), 0);
      return Math.max(50, Math.ceil((busiest * 2) / 10) * 10);
    }
    const installed = Number(this.solarConfig?.installedKwp);
    if (!(installed > 0)) return 0;
    return Math.ceil((installed * 2) / 10) * 10;
  }

  /** How the scaling reads on the tiles: in kWp where we know the array, as a multiple otherwise. */
  get scaleNote(): string {
    if (this.canSizeByKwp) {
      return '+' + (this.addKwp ?? 0) + ' kWp';
    }
    return 'at ' + Math.round(this.multiplier * 100) / 100 + 'x';
  }

  /** Generation the cap threw away, shown so the case for bigger inverters is visible. */
  clipped = 0;

  /** What the chosen multiplier means in things you can buy. Null where a figure is unknown. */
  extraKwp: number | null = null;

  extraPanels: number | null = null;

  extraRoofM2: number | null = null;

  roofShortfallM2: number | null = null;

  importAvoidedKwh: number | null = null;

  annualisedSaving: number | null = null;

  savingWindowDays = 0;

  /**
   * Why a battery cannot be modelled right now.
   *
   * An @Input because the hourly fetch is still the chart's. When that moves in here this goes back
   * to being ordinary state, which is the only reason it is worth a comment.
   */
  @Input() batteryUnavailableReason: string | null = null;

  /**
   * The one battery being modelled, recomputed whenever anything moves. Null until somebody asks
   * for a battery.
   *
   * Used to be an array, one row per swept size, drawn as a table of sizes we had picked. Donal's
   * call on 17/09/26 ended that: he specifies the system, so a ladder of our own sizes answered a
   * question nobody had asked and buried the single row he actually wanted.
   */
  batteryRow: BatteryResult | null = null;

  /**
   * What the quoted battery stored, per chart bar, in the chart's own buckets.
   *
   * Donal's ask from 04/09/26: the blue band is export, and he wants on-site charging drawn inside
   * it so battery capture reads distinctly from units genuinely sold to the grid. Same total, split
   * in two - stored solar nearest the axis, export left over beyond it.
   */
  private captureByBucket: number[] = [];

  /**
   * What the battery did on the IMPORT side of the axis, per chart bar.
   *
   * Donal's ask from 11/09/26: charge and discharge should read as their own thing on the chart, so
   * night charging is visible. The export side already told half the story - solar going into the
   * battery - and the other half was invisible, because a battery discharging simply makes the red
   * import bar shorter and buying at night simply makes it taller, with nothing to say why.
   */
  private dischargeByBucket: number[] = [];

  private gridChargeByBucket: number[] = [];

  /** Legend and stack label for the stored-solar band. */
  static readonly BATTERY_BAND = 'Spare solar into battery';

  /**
   * Import the battery covered, so it never had to be bought.
   *
   * Carved OUT of the metered import rather than added to it: the bar total is unchanged and the
   * segment simply splits, the same way the export band splits what was sold. The site used what it
   * used; the battery only changes where it came from.
   */
  static readonly DISCHARGE_BAND = 'Supplied by battery';

  /**
   * Bought from the grid overnight to fill the battery for the next day.
   *
   * The one band that ADDS height rather than splitting what is there, because it is genuinely extra
   * import that would not have happened without a battery. Drawing it any other way would hide the
   * cost of the trade and leave night charging looking free.
   *
   * Zero unless the site has "Also charge at night" ticked, which Lee Strand currently does not.
   */
  static readonly GRID_CHARGE_BAND = 'Bought overnight for the battery';

  /**
   * The connection-charge case, which at a site that already self-consumes its solar is the leg that
   * actually pays. ESB bills for reserved import capacity every year whether it is used or not, so
   * holding the peak down lets the site carry a smaller MIC or avoid being pushed to a bigger one.
   */
  peak: PeakShaveResult | null = null;

  /**
   * Peak is derived from the hourly series, so it is an hourly AVERAGE and reads lower than the
   * quarter-hour maximum ESB actually sets MIC from. Measured against Lee Strand's 15-minute data the
   * gap is about 5%, so the panel shows the figure and says what it is rather than quietly inflating
   * it. The honest fix is half-hourly or quarter-hourly demand data from MRSO.
   */
  static readonly PEAK_BASIS_NOTE =
    'Worked out from active power, so it is a floor rather than the billed figure: apparent power '
    + 'is always the higher of the two. Metering apparent power here would give the real number.';

  /**
   * What the panel is standing on, which decides whether it needs a caveat at all.
   *
   * With a kVA meter the figure is the billed quantity, measured, on a date. Without one it is
   * derived from active power and understated twice over - an hourly mean rather than the
   * quarter-hour maximum ESB set MIC from, and real power rather than apparent - so it says so.
   */
  get peakBasisNote(): string {
    if (!this.micFromMeasuredKva) return WhatIfPanelComponent.PEAK_BASIS_NOTE;

    const src = this.measured?.sourceName;
    return 'Measured apparent power' + (src ? ' from ' + src : '')
      + ', which is what the capacity charge is levied on. Highest hour in the window shown.';
  }

  /** The client's own MIC where they have entered one, else DCS's back-end figure for the site. */
  get micKva(): number | null {
    const m = this.siteMic?.micKva ?? this.solarConfig?.micKva;
    return m != null && Number(m) > 0 ? Number(m) : null;
  }

  get micChargePerKvaYear(): number | null {
    const c = this.solarConfig?.micCapacityChargeEurPerKvaYear;
    return c != null && Number(c) > 0 ? Number(c) : null;
  }

  /** Spare capacity today: what is reserved, less what the site actually draws. */
  get micHeadroomKva(): number | null {
    const peak = this.demandPeakKva;
    if (this.micKva === null || peak === null) return null;
    return this.micKva - peak;
  }

  /** The site's own apparent-power reading, where it has one. */
  get measuredDemand(): MeasuredDemandVM | null {
    return this.measured ?? null;
  }

  /** True when the panel's numbers come from a kVA meter rather than from active power. */
  get micFromMeasuredKva(): boolean {
    return this.measured?.peakKva != null && Number(this.measured.peakKva) > 0;
  }

  /**
   * The peak to compare against MIC, in kVA.
   *
   * Measured where possible. Otherwise the active-power peak, which is a LOWER BOUND on apparent
   * power rather than an estimate of it, since kVA >= kW always.
   */
  get demandPeakKva(): number | null {
    if (this.micFromMeasuredKva) return Number(this.measured!.peakKva);
    return this.peak ? this.peak.peakBeforeKw : null;
  }

  /** When the measured peak happened, so the figure can be quoted with a date. */
  get demandPeakAt(): Date | null {
    const at = this.measured?.peakAt;
    return at ? new Date(at) : null;
  }

  /** Times the site sat at or above its reserved capacity. Recurrence is the case, not one spike. */
  get micHoursAtOrOver(): number {
    return this.measured?.hoursAtOrOverMic ?? 0;
  }

  /**
   * Power factor at the peak, from the meter: mean kW over mean kVA across the peak's own hour.
   *
   * Both halves are means over the same hour, so the ratio is sound even though the peak itself is
   * an instant within it. Null where the site has no active-energy series to pair with the kVA.
   */
  get pfAtPeak(): number | null {
    const m = this.measured;
    if (!m || m.peakHourKwh == null || !m.peakHourMeanKva) return null;

    const kva = Number(m.peakHourMeanKva);
    const kw = Number(m.peakHourKwh);
    if (!(kva > 0) || !(kw > 0)) return null;

    // A meter can read marginally over unity on a partial interval. Clamp rather than reject: the
    // physical answer is 1, and refusing would drop the whole reactive split over a rounding error.
    return Math.min(1, kw / kva);
  }

  /**
   * Reactive power at the peak, in kVAr. The part of the load a battery does NOT remove.
   *
   * kVA is the hypotenuse of kW and kVAr, so kVAr = sqrt(kVA^2 - kW^2).
   */
  get kvarAtPeak(): number | null {
    const peak = this.demandPeakKva;
    const pf = this.pfAtPeak;
    if (peak === null || pf === null) return null;

    const kw = peak * pf;
    const sq = peak * peak - kw * kw;
    return sq > 0 ? Math.sqrt(sq) : 0;
  }

  /**
   * Capacity the battery frees up, in kVA, rounded DOWN to a whole unit. ESB does not sell
   * fractions, and rounding down keeps the euro figure on the safe side of what could be claimed.
   *
   * NOT simply the kW shaved, which is what this used to return and was wrong.
   *
   * A battery discharges REAL power. It pulls kW down and leaves kVAr exactly where it was, so the
   * power factor gets worse as the peak comes down and the kVA saved is always LESS than the kW
   * shaved. On Lee Strand's peak, 130 kW off a 519 kVA peak at 0.85 frees about 104 kVA, not 130.
   * Reporting the kW figure overstated the saving by roughly a quarter.
   *
   *     kVAr stays put:  kVA_after = sqrt((kW_before - shaved)^2 + kVAr^2)
   *
   * Without a power factor there is nothing to split, so it falls back to the old behaviour and the
   * panel says the figure is approximate.
   */
  get micFreedKva(): number | null {
    if (!this.peak || this.peak.shavedKw <= 0) return null;

    const peak = this.demandPeakKva;
    const pf = this.pfAtPeak;
    const kvar = this.kvarAtPeak;

    if (peak === null || pf === null || kvar === null) {
      return Math.floor(this.peak.shavedKw);
    }

    const kwBefore = peak * pf;
    const kwAfter = Math.max(0, kwBefore - this.peak.shavedKw);
    const kvaAfter = Math.sqrt(kwAfter * kwAfter + kvar * kvar);

    return Math.max(0, Math.floor(peak - kvaAfter));
  }

  /** The peak that remains after the battery, in kVA, to sit beside the before figure. */
  get micPeakAfterKva(): number | null {
    const peak = this.demandPeakKva;
    const freed = this.micFreedKva;
    return peak !== null && freed !== null ? peak - freed : peak;
  }

  /**
   * Why the freed capacity is less than the kW shaved. Worth saying out loud, because the two
   * numbers sitting side by side otherwise look like an arithmetic error.
   */
  get micReactiveNote(): string | null {
    const pf = this.pfAtPeak;
    if (!this.peak || this.peak.shavedKw <= 0 || pf === null) return null;

    const shaved = Math.round(this.peak.shavedKw);
    const freed = this.micFreedKva;
    if (freed === null || freed >= shaved) return null;

    return `The battery takes ${shaved} kW off the peak but frees ${freed} kVA, because it supplies `
      + `real power only. The reactive part stays, so the power factor of ${pf.toFixed(2)} at the `
      + `peak works against it.`;
  }

  /** What that freed capacity is worth per year. Null without a capacity charge on record. */
  get micSavingPerYear(): number | null {
    const freed = this.micFreedKva;
    const rate = this.micChargePerKvaYear;
    return freed !== null && rate !== null ? freed * rate : null;
  }

  /** The battery being modelled, or null where none has been asked for, so the answer never credits
   *  a battery nobody chose - the same battery is what "to spend" below is priced against. */
  private get configuredBatteryRow(): BatteryResult | null {
    return this.batteryRow;
  }

  /**
   * The battery's euro a year. Charged from solar, it is turned into a year the same way as the
   * solar saving (seasonFactor), over the hours the array was running: it can only store what the
   * sun made. With night charging on it stays by days, as the simulation gives it; that figure is
   * not shown (see showPricedResults).
   */
  get batteryEurPerYear(): number | null {
    const b = this.configuredBatteryRow;
    if (!b) return null;
    if (this.nightCharge || b.netEur == null || !this.hourlyRows.length) return b.netEurPerYear;

    const rows = this.hourlyRows;
    const step = rows.length > 1 ? rows[1].ts.getTime() - rows[0].ts.getTime() : 3600000;
    const end = new Date(rows[rows.length - 1].ts.getTime() + step);
    const solarFrom = this.scaledSeriesSpan()?.start;
    const start = solarFrom && solarFrom > rows[0].ts && solarFrom < end ? solarFrom : rows[0].ts;

    const season = this.seasonFactor(start, end);
    return season !== null ? b.netEur * season : b.netEurPerYear;
  }

  /** Every euro a year this scenario is worth, from whichever of the three streams above are known.
   *  Null only when none of them are - not the same as any one of them being zero. */
  get totalAnnualSavingEur(): number | null {
    const battery = this.configuredBatteryRow;
    const parts = [this.annualisedSaving, this.batteryEurPerYear, this.micSavingPerYear];
    const known = parts.filter((v): v is number => v !== null);
    return known.length ? known.reduce((a, b) => a + b, 0) : null;
  }

  /**
   * Capital cost of what the slider is currently proposing: the ADDED solar at its own per-kWp rate,
   * plus the quoted battery's real price where one is showing.
   *
   * All-or-nothing, unlike the saving above, and deliberately the opposite choice: a spend figure
   * missing a real component reads as cheaper than the scenario actually is, and a saving figure
   * missing a component only reads as more conservative than it actually is. Understating a cost is
   * the dangerous direction, understating a saving is not.
   */
  get totalSpendEur(): number | null {
    const kwp = this.extraKwp ?? 0;
    let solarSpend = 0;

    if (kwp > 0) {
      const rate = this.solarConfig?.solarCostPerKwpEur;
      if (rate == null || Number(rate) <= 0) return null;
      solarSpend = kwp * Number(rate);
    }

    const battery = this.configuredBatteryRow;
    if (battery) {
      if (battery.costEur == null) return null;
      return solarSpend + battery.costEur;
    }

    return solarSpend;
  }

  /**
   * Whether the client has actually proposed anything, and so whether there is an answer to show.
   *
   * Used to be "the solar slider moved", which quietly made a battery-only scenario unanswerable -
   * and "is the battery worth it without adding panels" is precisely the question Donal asked on
   * 17/09/26. Either lever counts now.
   */
  get hasScenario(): boolean {
    return this.multiplier !== 1 || this.batteryKwh !== null;
  }

  /** Whether the Results strip shows euro at all: not with night charging on, where the battery's
   *  saving leaves out its wear (see the Results comment in the template). */
  get showPricedResults(): boolean {
    return this.hasScenario && !this.nightCharge
      && (this.totalAnnualSavingEur !== null || (this.totalSpendEur ?? 0) > 0 || this.percentOffGrid !== null);
  }

  /** Straight-line payback: spend over saving, no discounting. Null where either half is unknown, or
   *  where the saving is zero or negative and "years" would mean nothing. */
  get simplePaybackYears(): number | null {
    const spend = this.totalSpendEur;
    const saving = this.totalAnnualSavingEur;
    return spend !== null && saving !== null && saving > 0 ? spend / saving : null;
  }

  /**
   * Share of site consumption NOT bought from the grid, with the scenario applied. totals.grossImport
   * already reflects the solar scaling (applyMultiplier scales the chart's own series), so only the
   * battery's own contribution needs adding on top - it discharges into hours the chart totals still
   * count as imported, because the battery simulation runs separately from the chart.
   */
  get percentOffGrid(): number | null {
    const consumption = this.totals?.siteConsumption;
    if (!consumption || consumption <= 0) return null;

    const batteryAvoided = this.configuredBatteryRow?.importAvoidedKwh ?? 0;
    const effectiveImport = Math.max(0, (this.totals?.grossImport ?? 0) - batteryAvoided);

    return Math.max(0, Math.min(100, (1 - effectiveImport / consumption) * 100));
  }

  /** Plain-English reason the shaving stopped where it did. */
  get micLimitReason(): string | null {
    if (!this.peak || this.peak.shavedKw <= 0) return null;
    if (this.peak.limitedBy === 'power') {
      return 'Limited by how fast the battery can discharge, not by its size - a stronger unit would '
        + 'shave more, a bigger one would not.';
    }
    if (this.peak.limitedBy === 'energy') {
      return 'Limited by capacity - the battery empties partway through a peak, so more storage '
        + 'would shave more.';
    }
    if (this.peak.limitedBy === 'shape') {
      return 'Limited by the site\'s own load, not the battery - there is too little quiet time '
        + 'between peaks to recharge, so a larger unit would not shave much more.';
    }
    return null;
  }

  /**
   * Shown wherever there is a peak to measure, MIC or not: the client enters the MIC here, so a site
   * with none recorded is exactly where the box is needed (Jayden, 02/10/26).
   */
  get showMicPanel(): boolean {
    return this.peak !== null && this.hourlyLoaded;
  }

  /**
   * The battery's size in kWh, exactly as typed, the same way the solar is typed in kWp (Jayden,
   * 28/09/26). It replaced a dropdown of cabinet sizes times a count: any size can be asked about
   * now, including whatever the sizing guide recommends, which is rarely a whole number of cabinets.
   *
   * Zero is the opening position: solar on its own, nothing bought yet. Deliberately NOT seeded from
   * the site (Donal, 17/09/26): the simulation models the system being proposed, not whatever battery
   * happens to be recorded against the site.
   *
   * Nullable because an empty box is a real state mid-edit, and nothing here writes the box back -
   * see onBatteryChange.
   */
  batteryKwhEntry: number | null = 0;

  /** The largest size simulated. Far beyond any site DCS meters; it only stops a slip of the keyboard. */
  static readonly MAX_BATTERY_KWH = 10000;

  /** Size actually simulated. Null when no battery has been asked for. */
  get batteryKwh(): number | null {
    const kwh = Number(this.batteryKwhEntry);
    return isFinite(kwh) && kwh > 0 ? Math.min(kwh, WhatIfPanelComponent.MAX_BATTERY_KWH) : null;
  }

  /**
   * How fast the battery being simulated can give power back, in kW, for the line under the box.
   * The same scaling the simulation uses: the quoted battery's own rate per kWh where the site has
   * one, else 0.5 kW per kWh.
   */
  get batteryPowerKw(): number | null {
    const kwh = this.batteryKwh;
    if (kwh === null) return null;
    const c = this.solarConfig;
    return scalePower(kwh,
      c?.batteryCapacityKwh ? Number(c.batteryCapacityKwh) : null,
      c?.batteryDischargeKw ? Number(c.batteryDischargeKw) : null,
      0.5);
  }

  /**
   * Either battery control moving changes the stack as well as the sums, so both come through here.
   *
   * Deliberately does NOT clamp the box itself. Rewriting the model on every keystroke turns a
   * cleared field into a "0" you then have to delete again before you can type 3, which is exactly
   * the kind of small fight nobody notices until they are demoing. batteryCountUsed does the
   * clamping where it matters instead.
   */
  onBatteryChange(): void {
    this.applyMultiplier();
  }

  private get bandCapacityKwh(): number | null {
    return this.batteryKwh;
  }

  /** True when a battery has been asked for and there are hourly readings to model it on. */
  get showBatteryBand(): boolean {
    return this.hourlyLoaded && this.bandCapacityKwh !== null;
  }

  /**
   * Connection size folds away, on Jayden's call 11/09/26: it is the most technical block on the
   * panel and reads as intimidating to anyone who is not an electrician. The header still carries
   * the verdict, so nobody has to open it to find out whether there is a problem.
   */
  showMic = false;

  // ---- Tariffs ----------------------------------------------------------------------------------

  /**
   * The rates as the fold shows them, in euro per kWh, while they are being edited.
   *
   * Jayden, 02/10/26: the client sets the rates they want simulated, from here, instead of DCS
   * filling in a page per site. Nothing is priced from these rows until Update saves them; the
   * saved set (tariffRates) is what the what-if uses, for everyone looking at the site.
   */
  rateRows: RateRow[] = [];

  showRates = false;
  ratesSaving = false;
  ratesSaved = false;
  ratesError: string | null = null;

  readonly hourOptions = Array.from({ length: 24 }, (_, h) => h);

  toggleRates(): void {
    this.showRates = !this.showRates;
  }

  /** Whether the client has saved rates of their own, as opposed to looking at DCS's. */
  get ratesAreTheirs(): boolean {
    return this.tariffRates.length > 0;
  }

  canAddRate(kind: RateKind): boolean {
    if (this.rateRows.length >= 12) return false;
    return kind === 'other' || !this.rateRows.some(r => r.kind === kind);
  }

  addRate(kind: RateKind): void {
    if (!this.canAddRate(kind)) return;
    const hours: Record<RateKind, [number | null, number | null]> = {
      day: [null, null],
      night: [DEFAULT_NIGHT_FROM_HOUR, DEFAULT_NIGHT_TO_HOUR],
      evening: [17, 19],
      other: [12, 14]
    };
    this.rateRows = [...this.rateRows, {
      kind, name: RATE_NAMES[kind], eur: null, fromHour: hours[kind][0], toHour: hours[kind][1]
    }];
    this.onRateEdit();
  }

  removeRate(index: number): void {
    if (this.rateRows[index]?.kind === 'day') return;
    this.rateRows = this.rateRows.filter((_, i) => i !== index);
    this.onRateEdit();
  }

  onRateEdit(): void {
    this.ratesSaved = false;
    this.ratesError = null;
  }

  hourLabel(h: number): string {
    return (h < 10 ? '0' : '') + h + ':00';
  }

  /** Save the rows as the site's rates and start pricing with them. */
  saveRates(): void {
    const missing = this.rateRows.find(r => r.eur === null || r.eur === undefined || isNaN(Number(r.eur)) || Number(r.eur) < 0);
    if (missing) {
      this.ratesError = `Enter a rate for ${missing.name || RATE_NAMES[missing.kind]}.`;
      return;
    }
    this.sendRates(this.rateRows.map(r => ({
      kind: r.kind,
      name: r.name,
      // Euro to cent, rounded to the four places the database keeps, so 0.2610 is stored as 26.1.
      centPerKwh: Math.round(Number(r.eur) * 1000000) / 10000,
      fromHour: r.kind === 'day' ? undefined : (r.fromHour ?? undefined),
      toHour: r.kind === 'day' ? undefined : (r.toHour ?? undefined)
    })));
  }

  /** Clear the site's own rates, so DCS's back-end figures price it again. */
  useDcsRates(): void {
    this.sendRates([]);
  }

  private sendRates(rates: TariffRateVM[]): void {
    if (!this.siteId || this.ratesSaving) return;
    this.ratesSaving = true;
    this.ratesError = null;
    this.cdr.markForCheck();

    this.tariffService.saveRates$(this.siteId, rates).subscribe({
      next: saved => {
        this.ratesSaving = false;
        this.ratesSaved = true;
        this.tariffRates = saved ?? [];
        this.rateRows = this.rowsFromSite();
        this.recompute();
      },
      error: err => {
        this.ratesSaving = false;
        this.ratesError = err?.error?.message || 'The rates could not be saved. Try again in a moment.';
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * The rows the fold starts from: the client's saved rates, else DCS's back-end ones for the site
   * shown as a starting point (a day rate, and a night rate on the default hours where there is one).
   */
  private rowsFromSite(): RateRow[] {
    if (this.tariffRates.length) {
      return this.tariffRates.map(r => ({
        kind: (r.kind as RateKind) ?? 'other',
        name: r.name ?? '',
        eur: r.centPerKwh != null ? Number(r.centPerKwh) / 100 : null,
        fromHour: r.fromHour ?? null,
        toHour: r.toHour ?? null
      }));
    }

    const c = this.solarConfig;
    const day = c?.importDayCentPerKwh ?? c?.importCentPerKwh;
    const rows: RateRow[] = [{
      kind: 'day', name: RATE_NAMES.day, eur: day != null ? Number(day) / 100 : null, fromHour: null, toHour: null
    }];
    if (c?.importNightCentPerKwh != null) {
      rows.push({
        kind: 'night', name: RATE_NAMES.night, eur: Number(c.importNightCentPerKwh) / 100,
        fromHour: DEFAULT_NIGHT_FROM_HOUR, toHour: DEFAULT_NIGHT_TO_HOUR
      });
    }
    return rows;
  }

  /** The client's saved rates as the simulation takes them, or null where they have none saved. */
  private get bands(): TariffBand[] | null {
    if (!this.tariffRates.length) return null;
    return this.tariffRates.map(r => ({
      kind: (r.kind as RateKind) ?? 'other',
      cent: Number(r.centPerKwh ?? 0),
      fromHour: r.fromHour ?? null,
      toHour: r.toHour ?? null
    }));
  }

  /** Whether any night rate is in play, the client's or DCS's. Decides the night shading. */
  get hasNightRate(): boolean {
    const bands = this.bands;
    return bands ? bands.some(b => b.kind === 'night') : this.solarConfig?.importNightCentPerKwh != null;
  }

  /** "23:00" and "08:00", for the night charging hint. */
  get nightFromLabel(): string { return this.hourLabel(nightHours(this.bands).from); }
  get nightToLabel(): string { return this.hourLabel(nightHours(this.bands).to); }

  /**
   * What the simulation prices with. The client's rates when saved, which also set the night window
   * and the cost of the units bought at night; otherwise the back-end rates, as before.
   */
  private rateInputs(): { dayCent: number | null; weekendDayCent: number | null; nightCent: number | null; exportCent: number | null; rates: TariffBand[] | null } {
    const c = this.solarConfig;
    const bands = this.bands;
    const exportCent = c?.exportCentPerKwh != null ? Number(c.exportCentPerKwh) : null;

    if (bands) {
      return {
        dayCent: bands.find(b => b.kind === 'day')?.cent ?? null,
        weekendDayCent: null,
        nightCent: bands.find(b => b.kind === 'night')?.cent ?? null,
        exportCent,
        rates: bands
      };
    }

    return {
      dayCent: c?.importDayCentPerKwh != null ? Number(c.importDayCentPerKwh) : null,
      weekendDayCent: c?.importWeekendDayCentPerKwh != null ? Number(c.importWeekendDayCentPerKwh) : null,
      nightCent: c?.importNightCentPerKwh != null ? Number(c.importNightCentPerKwh) : null,
      exportCent,
      rates: null
    };
  }

  toggleMic(): void {
    this.showMic = !this.showMic;
  }

  // ---- MIC entry ---------------------------------------------------------------------------------

  /**
   * The box in the Connection size fold. Jayden, 02/10/26: the client enters the site's MIC here and
   * presses Update; it is stored against the site with every earlier figure kept, and the newest is
   * the one in use. Nothing changes until it is saved.
   */
  micEntry: number | null = null;

  micSaving = false;
  micSaved = false;
  micError: string | null = null;

  /** Whether the MIC in use is one the client entered, as opposed to DCS's. */
  get micIsTheirs(): boolean {
    return this.siteMic != null;
  }

  /** When the client's MIC was entered, for the line under the box. */
  get micEnteredOn(): Date | null {
    const at = this.siteMic?.enteredUtc;
    return at ? new Date(at) : null;
  }

  onMicEdit(): void {
    this.micSaved = false;
    this.micError = null;
  }

  saveMic(): void {
    if (!this.siteId || this.micSaving) return;

    const kva = Number(this.micEntry);
    if (this.micEntry === null || this.micEntry === undefined || String(this.micEntry) === '' || !(kva > 0)) {
      this.micError = 'Enter the MIC in kVA. It is on the electricity bill.';
      return;
    }

    this.micSaving = true;
    this.micError = null;
    this.cdr.markForCheck();

    // The measured demand's own window, so the hours at or over the new figure are counted for the
    // same period as the rest of the fold.
    const m = this.measured;
    this.micService.saveMic$(this.siteId, kva, m?.windowFrom, m?.windowTo).subscribe({
      next: res => {
        this.micSaving = false;
        this.micSaved = true;
        this.siteMic = res?.mic ?? null;
        this.micEntry = this.micKva;
        if (m && res?.hoursAtOrOverMic != null) {
          this.measured = { ...m, hoursAtOrOverMic: res.hoursAtOrOverMic };
        }
        this.cdr.markForCheck();
      },
      error: err => {
        this.micSaving = false;
        this.micError = err?.error?.message || 'The MIC could not be saved. Try again in a moment.';
        this.cdr.markForCheck();
      }
    });
  }

  /** Battery details fold away the same way, on Jayden's call 24/09/26. Closed until asked for. */
  showBattDetails = false;

  toggleBattDetails(): void {
    this.showBattDetails = !this.showBattDetails;
  }

  /**
   * Drawn at the root of the template and positioned fixed, NOT beside the badge that opened it.
   *
   * The what-if panel is `overflow-y: auto`, so a bubble drawn inside it in the document flow is
   * clipped by the panel exactly when the panel is tall enough to need scrolling, which is when a
   * tooltip is most likely to be wanted. Fixed positioning takes its containing block from the
   * viewport instead, so it escapes the scroll box entirely.
   */
  tip: string | null = null;

  tipX = 0;

  tipY = 0;

  tipAbove = false;

  /** Short on purpose. A tooltip nobody finishes reading has not explained anything. */
  readonly tipKwp =
    'Kilowatt peak is what panels are rated to produce. One commercial panel is about 0.6 kWp.';

  readonly tipMic =
    'The size of the ESB connection, in kVA. You pay for the size you reserve every month, used or not.';

  showTip(event: Event, text: string): void {
    const badge = event.currentTarget as HTMLElement | null;
    if (!badge) return;

    const box = badge.getBoundingClientRect();
    const width = 240;

    // Hung off the badge's right edge, then pulled back inside the panel. Left to the badge alone
    // it lands out over the chart, which reads as belonging to the chart rather than to the control
    // that opened it.
    const panel = badge.closest('.rsc-whatif') as HTMLElement | null;
    const bounds = panel ? panel.getBoundingClientRect() : null;

    let x = box.right - width;
    if (bounds) {
      x = Math.min(x, bounds.right - width - 10);
      x = Math.max(x, bounds.left + 10);
    }
    x = Math.max(8, Math.min(window.innerWidth - width - 8, x));

    // Flipped above the badge when there is not room below it. Connection size sits at the bottom
    // of the panel, so this is the normal case there rather than an edge case.
    this.tipAbove = box.bottom + 110 > window.innerHeight;
    let y = this.tipAbove ? box.top - 8 : box.bottom + 8;

    // A fixed element is only positioned against the viewport while nothing above it has a
    // transform. The dialog this panel lives in does, so the coordinates have to be rebased onto
    // whichever ancestor is actually acting as the containing block, or the bubble lands a couple
    // of hundred pixels away from the badge that opened it.
    const origin = this.fixedOrigin(badge);
    x -= origin.x;
    y -= origin.y;

    this.tipX = x;
    this.tipY = y;
    this.tip = text;
  }

  /**
   * Top left of the nearest ancestor that establishes a containing block for fixed positioning,
   * or the viewport origin where there is none. transform, filter, perspective and a promised
   * transform all do it, which is why this walks rather than assuming the dialog.
   */
  private fixedOrigin(from: HTMLElement): { x: number; y: number } {
    let el = from.parentElement;

    while (el) {
      const style = getComputedStyle(el);
      const establishes = style.transform !== 'none'
        || style.filter !== 'none'
        || style.perspective !== 'none'
        || style.willChange.indexOf('transform') >= 0;

      if (establishes) {
        const box = el.getBoundingClientRect();
        return { x: box.left, y: box.top };
      }

      el = el.parentElement;
    }

    return { x: 0, y: 0 };
  }

  hideTip(): void {
    this.tip = null;
  }

  resetMultiplier(): void {
    this.multiplier = 1;
    this.applyMultiplier();
  }

  /** True once there are hourly readings to model a battery against. The controls show from then on,
   *  whether or not a battery has been chosen yet - hiding them is what made the old version's
   *  options invisible. */
  get canShowBattery(): boolean {
    return this.hourlyLoaded && !this.batteryUnavailableReason;
  }

  /**
   * Simulate the chosen battery at the current multiplier. Cheap enough to redo on every slider
   * move - one pass over ~5,800 rows, so single-digit milliseconds.
   */
  private runBattery(): void {
    this.batteryRow = null;

    if (!this.hourlyLoaded || !this.hourlyRows.length) return;

    const c = this.solarConfig;

    // Two different sizes, and keeping them apart is the whole trick. The QUOTE is a reference
    // point only: the price per kWh and the C-rate are read off it, so it must not move when the
    // client picks a different system. The CHOSEN size is the only thing simulated.
    const quoted = c?.batteryCapacityKwh ? Number(c.batteryCapacityKwh) : null;
    const chosen = this.batteryKwh;

    // No battery asked for. Not an error state: it is the opening position, solar on its own.
    if (chosen === null) return;

    // 0.5C is the usual shape of a commercial battery quote - a 200 kWh unit on a 100 kW inverter -
    // and is only used where the site has no configured power figures to read a C-rate from.
    const FALLBACK_C_RATE = 0.5;

    // A quote is nearly always an outright price for a set number of cabinets, and cabinets are sold
    // with volume discounts, so the per-unit price on the Batteries page cannot reproduce it - three
    // EnerArk cabinets list at 3 x 68,500 but were quoted at 172,500. The quote is therefore typed
    // into the installed cost, and the rate this site is really paying is that price over that
    // capacity. Work it back out rather than asking for the same number twice. An explicitly entered
    // per-kWh rate still wins, for a quote that genuinely is priced by the kWh.
    const quotedPerKwh = c?.batteryCostEur != null && quoted != null && quoted > 0
      ? Number(c.batteryCostEur) / quoted
      : null;

    const result = simulateBattery({
      hours: this.hourlyRows,
      multiplier: Number(this.multiplier) || 1,
      addedKwp: this.estimatedKwp,
      // No inverter limit: see effectiveCap.
      inverterKw: null,
      capacityKwh: chosen,
      chargeKw: scalePower(chosen, quoted, c?.batteryChargeKw ? Number(c.batteryChargeKw) : null, FALLBACK_C_RATE),
      dischargeKw: scalePower(chosen, quoted, c?.batteryDischargeKw ? Number(c.batteryDischargeKw) : null, FALLBACK_C_RATE),
      roundTripPct: c?.batteryRoundTripPct ? Number(c.batteryRoundTripPct) : 90,
      allowGridCharge: this.nightCharge,
      dispatchOnClock: this.nightCharge,
      nightChargeToPct: c?.batteryNightChargeToPct != null ? Number(c.batteryNightChargeToPct) : null,
      ...this.rateInputs()
    });

    this.batteryRow = priceBattery(
      result,
      // Land exactly on the quoted size and it is priced at the quote itself, exactly as entered.
      // Any other size goes at a rate, because an outright figure only prices the one system it was
      // given for.
      quoted != null && chosen === quoted && c?.batteryCostEur != null ? Number(c.batteryCostEur) : null,
      c?.batteryCostPerKwhEur != null ? Number(c.batteryCostPerKwhEur) : quotedPerKwh
    );
  }

  /**
   * Peak demand with and without the quoted battery.
   *
   * Runs even when no battery is configured: the peak on its own answers "are they paying for more
   * connection than they use", which is worth showing before anyone buys anything. With no battery
   * the shave is simply zero.
   */
  private runPeak(): void {
    if (!this.hourlyLoaded || !this.hourlyRows.length) {
      this.peak = null;
      return;
    }

    const c = this.solarConfig;
    const capacity = this.bandCapacityKwh ?? 0;
    const FALLBACK_C_RATE = 0.5;

    // Same reasoning as traceFlowByBucket: the C-rate comes off the quoted pair, never off the size
    // the client happens to have chosen.
    const quoted = c?.batteryCapacityKwh ? Number(c.batteryCapacityKwh) : null;

    this.peak = shavePeak({
      hours: this.hourlyRows,
      multiplier: Number(this.multiplier) || 1,
      addedKwp: this.estimatedKwp,
      // No inverter limit: see effectiveCap.
      inverterKw: null,
      capacityKwh: capacity,
      chargeKw: scalePower(capacity, quoted, c?.batteryChargeKw ? Number(c.batteryChargeKw) : null, FALLBACK_C_RATE),
      dischargeKw: scalePower(capacity, quoted, c?.batteryDischargeKw ? Number(c.batteryDischargeKw) : null, FALLBACK_C_RATE),
      roundTripPct: c?.batteryRoundTripPct ? Number(c.batteryRoundTripPct) : 90
    });
  }

  /**
   * Simulate the quoted battery once more, this time keeping the per-interval trace, and fold what
   * it stored into the chart's own buckets.
   *
   * The trace is hourly whatever the chart is showing, because that is the only resolution a battery
   * means anything at. Folding hours into the displayed bars is therefore a real step, not a
   * formality: a monthly bar collects roughly 730 traced hours.
   *
   * Buckets are matched on their start instants rather than on a formatted key, so this works for
   * day, week, month, quarter and year bars without knowing which one is in play.
   */
  private traceFlowByBucket(): { captured: number[]; discharged: number[]; gridCharged: number[] } {
    const capacity = this.bandCapacityKwh;
    const buckets = this.bucketDates;
    const empty = { captured: [], discharged: [], gridCharged: [] };

    if (capacity === null || !this.hourlyLoaded || !this.hourlyRows.length || !buckets.length) {
      return empty;
    }

    const c = this.solarConfig;
    const FALLBACK_C_RATE = 0.5;

    // Power scales off the QUOTED pair, exactly as runBattery does. Passing the chosen capacity as
    // its own reference, which is what this used to do, handed a 200 kWh battery the full 360 kW the
    // site's 723 kWh quote is rated at, so the bands on the chart were drawn from a faster battery
    // than the euro figures beside them were.
    const quoted = c?.batteryCapacityKwh ? Number(c.batteryCapacityKwh) : null;

    const result = simulateBattery({
      hours: this.hourlyRows,
      multiplier: Number(this.multiplier) || 1,
      addedKwp: this.estimatedKwp,
      // No inverter limit: see effectiveCap.
      inverterKw: null,
      capacityKwh: capacity,
      chargeKw: scalePower(capacity, quoted, c?.batteryChargeKw ? Number(c.batteryChargeKw) : null, FALLBACK_C_RATE),
      dischargeKw: scalePower(capacity, quoted, c?.batteryDischargeKw ? Number(c.batteryDischargeKw) : null, FALLBACK_C_RATE),
      roundTripPct: c?.batteryRoundTripPct ? Number(c.batteryRoundTripPct) : 90,
      allowGridCharge: this.nightCharge,
      dispatchOnClock: this.nightCharge,
      nightChargeToPct: c?.batteryNightChargeToPct != null ? Number(c.batteryNightChargeToPct) : null,
      ...this.rateInputs(),
      trace: true
    });

    const trace = result.flowPerHour ?? [];

    const captured = new Array<number>(buckets.length).fill(0);
    const discharged = new Array<number>(buckets.length).fill(0);
    const gridCharged = new Array<number>(buckets.length).fill(0);

    // Both sides are sorted, so one merge pass places every traced hour. A binary search per hour
    // would do the same job; this is O(n + m) and needs no bounds care.
    const starts = buckets.map(d => d.getTime());

    /*
     * Where the LAST bar stops.
     *
     * Without this the final bar was a bin for everything that came after it. The advance loop
     * below stops once b is the last index - there is no next start to compare against - so every
     * traced hour past the end of the displayed window kept landing on the last bar. The trace is
     * always the WHOLE hourly window whatever the chart is showing, so drilling into one day piled
     * the rest of the year's discharge onto that day's final hour. Discharge is carved out of the
     * metered import, so a bar carrying a year of it swallows its own import completely: that is
     * Donal's "the last bar is always fully supplied by the battery", exactly.
     *
     * Drilled, the step knows its own exclusive end. At the base level the server bucketed the same
     * window the hours came from, so the end of the readings is the honest bound.
     */
    const end = this.bucketWindowEnd ? this.bucketWindowEnd.getTime() : Number.POSITIVE_INFINITY;

    let b = 0;
    for (const t of trace) {
      const ts = t.ts.getTime();
      // Sorted, so once one hour is past the window every hour after it is too.
      if (ts >= end) break;

      while (b + 1 < starts.length && starts[b + 1] <= ts) b++;
      // An hour before the first bar belongs to no bar - the hourly fetch and the displayed window
      // can differ at the edges - so it is dropped rather than credited to bar zero.
      if (ts < starts[b]) continue;

      captured[b] += t.capturedKwh;
      discharged[b] += t.dischargedKwh;
      gridCharged[b] += t.gridChargedKwh;
    }

    return { captured, discharged, gridCharged };
  }

  /**
   * Split the grid segment of each bar into stored solar and the export that was left over.
   *
   * Only the negative side is touched. A positive grid segment is import, and a battery discharging
   * into it is a different story that the panel's figures already tell; drawing it here would put
   * two unrelated meanings in one colour.
   *
   * The band is emitted on EVERY bar, zero included. ngx-charts assigns ordinal colours by where a
   * label first appears across the results, so a band that only shows up on the third bar would
   * reorder the labels and hand the series each other's colours.
   */
  /**
   * Move the negative part of the grid segment into its own series, so export gets its own colour.
   *
   * A bucket is netted off, so a grid segment is either import or export, never both - which is why
   * this can move the whole segment rather than having to split a single value in two. Where the
   * battery band has already run, the value left on the grid segment is the export the battery
   * could not take, and that is what ends up here.
   *
   * The segment is REPLACED rather than zeroed and added alongside, because a zero-height segment
   * still earns a legend entry and a tooltip row.
   */
  /**
   * The battery's bands, described rather than drawn.
   *
   * Everything specific to a battery lives here and nowhere else in the render path. When the
   * what-if moves into its own component this method goes with it and arrives back as an @Input,
   * and nothing downstream has to change: applyBands already takes them as data.
   *
   * All three attach to the SAME source, the grid series exactly as it arrived, and the sign of that
   * segment picks which applies. Export is negative and import positive in one series, so a bucket
   * that sold gets the charging band and a bucket that bought gets the other two.
   */
  private batteryBands(gridName: string, series: ResourceSeriesVM[]): SeriesBand[] {
    return [
      {
        name: WhatIfPanelComponent.BATTERY_BAND,
        rgb: this.batteryBandRgb(series),
        source: gridName,
        mode: 'carve',
        side: 'inside',
        appliesWhen: 'negative',
        valueByBucket: this.captureByBucket
      },
      {
        name: WhatIfPanelComponent.DISCHARGE_BAND,
        rgb: this.dischargeBandRgb(series),
        source: gridName,
        mode: 'carve',
        side: 'inside',
        appliesWhen: 'positive',
        valueByBucket: this.dischargeByBucket
      },
      {
        // 'always', not 'positive'. Units bought overnight to fill the battery are extra import
        // whatever the bucket did on balance, and a day that exported overall can still have
        // charged at 02:00. At day or month granularity that is common, not exotic.
        //
        // 'inside', under the mains, not on top of it. Donal's ask from 24/09/26: solar, the
        // battery and night charging all sit below the mains, and the mains is always on top. It
        // is still extra height, because the band adds rather than carves, so the cost of the trade
        // stays visible. Applied after the discharge band, so it lands between that and the mains.
        name: WhatIfPanelComponent.GRID_CHARGE_BAND,
        rgb: this.gridChargeBandRgb(series),
        source: gridName,
        mode: 'add',
        side: 'inside',
        appliesWhen: 'always',
        valueByBucket: this.gridChargeByBucket
      }
    ];
  }

  /**
   * The solar colour both battery bands are drawn from: the generation series on the chart, or the
   * estimated array's on a site without one. Never just "the other series": on a stacked chart of
   * assets that would be a lighting board.
   */
  private solarRgb(series: ResourceSeriesVM[]): string | null {
    const gen = series.find(s => isGenerationName(s.name));
    return gen?.rgb ?? WhatIfPanelComponent.ESTIMATE_RGB;
  }

  /**
   * Colour for the stored-solar band: the solar colour, paled.
   *
   * Both battery bands stay in the solar colour's family, because it is one journey: what goes in on
   * a sunny afternoon comes back out that evening. They used to be the identical shade, and Jayden,
   * 09/10/26, found the battery unreadable: charging and supplying merged into one block and it did
   * not look used at all. So going in is pale and coming out is deep.
   */
  private batteryBandRgb(series: ResourceSeriesVM[]): string {
    return lighten(this.solarRgb(series), 0.5) ?? '#a5c8f5';
  }

  /**
   * Colour for import the battery covered: a blue of its own, as on the Tesco pictures Jayden
   * signed off (09/10/26), so the battery reads as a thing in its own right rather than a shade of
   * the solar. Green where the site's solar is itself blue (Lee Strand's is), so the two never merge.
   */
  private dischargeBandRgb(series: ResourceSeriesVM[]): string {
    const solar = parseRgb(this.solarRgb(series));
    const bluish = !!solar && solar[2] > solar[0] + 40 && solar[2] > solar[1];
    return bluish ? WhatIfPanelComponent.BATTERY_GREEN : WhatIfPanelComponent.BATTERY_BLUE;
  }

  static readonly BATTERY_BLUE = '#1F6FB2';
  static readonly BATTERY_GREEN = '#2E9E6B';

  /**
   * Colour for units bought overnight to fill the battery: the GRID colour, deepened.
   *
   * It stays in the grid's family because that is exactly what it is - electricity bought from the
   * grid, on the same bill as the rest of the red. Deepening it says "bought, but for the battery
   * rather than for the site", which is the only distinction worth drawing here.
   */
  private gridChargeBandRgb(series: ResourceSeriesVM[]): string {
    const grid = this.grid(series);
    return darken(grid?.rgb ?? null, 0.45) ?? '#7a1f0b';
  }

  /**
   * Whether this scenario charges the battery from the grid overnight.
   *
   * A scenario choice, not a site fact, so it lives here rather than following
   * SiteSolarConfigs.BatteryAllowGridCharge. Donal's question on 18/09/26 was "if I put a bigger
   * solar system in AND I use night rate charging, what does that do to my overall consumption" -
   * something he wants to flick on and off while looking at the chart, which a column on the site's
   * configuration cannot answer. Off to begin with, same as the battery itself.
   */
  nightCharge = false;

  get batteryNightChargeOn(): boolean {
    return this.nightCharge;
  }

  /** Units bought overnight that would not have been bought at all, a year. */
  get nightChargedKwhPerYear(): number | null {
    return this.batteryRow?.nightChargedKwhPerYear ?? null;
  }

  /** Units delivered back out of the battery, a year. Less than what went in, by the round trip. */
  get batteryDeliveredKwhPerYear(): number | null {
    return this.batteryRow?.dischargedKwhPerYear ?? null;
  }

  /**
   * Units lost to the round trip on the night-charged share, a year.
   *
   * Worth its own line because it is the thing that decides whether night charging is worth doing at
   * all, and it is invisible in either of the other two figures on its own. At 89% you buy about
   * nine units to get eight back, and that gap is bought at the night rate and never delivered.
   */
  get roundTripLossKwhPerYear(): number | null {
    const bought = this.nightChargedKwhPerYear;
    if (bought == null || bought <= 0) return null;

    const pct = this.solarConfig?.batteryRoundTripPct
      ? Number(this.solarConfig.batteryRoundTripPct)
      : 90;
    const roundTrip = Math.min(100, Math.max(1, pct)) / 100;

    // BOTH legs. The simulation splits the round trip as a square root across charging and
    // discharging, and it is tempting to reuse that sqrt here, but a unit bought at night pays the
    // loss twice, going in and coming back out. What never reaches the site is the full round trip.
    // One leg halves the figure and flatters the case.
    //
    // Only the night-charged share: stored solar loses energy the same way, but those units cost
    // nothing in the first place, so that loss is not money and does not belong on this line.
    return bought * (1 - roundTrip);
  }

  /**
   * Rebuild the chart with the scaled series. Site load is the invariant: scaling generation does
   * not change what the building used, it changes how much of it came off the roof. So for each
   * interval the grid series is recomputed as load - generation, which is what lets a big enough
   * multiplier push the grid negative and show export appearing.
   */
  /**
   * Inverter ceiling for one bar, in the chart's unit, or null where it cannot be applied honestly.
   *
   * A 200 kVA inverter caps a single HOUR at 200 kWh. It says nothing about a whole day: that
   * depends on how many hours the sun was up, which is not in the data. So clipping is only applied
   * where the bars really are hourly, detected from the gap between them rather than assumed.
   */
  /**
   * No inverter limit on the solar being modelled, on Jayden's call 09/10/26 (said twice on the
   * Donal catch-up). Capping an expansion at the inverter the site has today is misleading: an array
   * added in earnest comes with its own inverter, and at Lee Strand the 200 kW limit hid most of the
   * 300 kWp being proposed. What the existing inverter already clips is in the metered readings.
   *
   * Kept as the one place a limit would come back in, should a case for it come up; the clipping
   * code and the "Inverter limit" guide behind it stay dormant while this is null.
   */
  private get effectiveCap(): number | null {
    return null;
  }

  /**
   * Turns the multiplier into things a client can actually price: extra kWp, panel count, roof area,
   * grid import avoided and what that is worth. Every output is null where the figure it needs is
   * missing, so a half-filled config still shows what it can.
   */
  private deriveWhatItMeans(k: number): void {
    const c = this.solarConfig;
    const extraFactor = k - 1;

    this.extraKwp = this.newArray ? extraFactor : c?.installedKwp ? Number(c.installedKwp) * extraFactor : null;

    this.extraPanels = this.extraKwp !== null && c?.panelWatts
      ? Math.ceil((this.extraKwp * 1000) / Number(c.panelWatts))
      : null;

    this.extraRoofM2 = this.extraPanels !== null && c?.panelAreaM2
      ? this.extraPanels * Number(c.panelAreaM2)
      : null;

    this.roofShortfallM2 = this.extraRoofM2 !== null && c?.usableRoofM2
      ? Math.max(0, this.extraRoofM2 - Number(c.usableRoofM2))
      : null;

    // Import avoided is the honest saving: extra generation that got exported saves nothing at the
    // import rate, and may earn nothing at all.
    const baseImport = this.baseTotals?.grossImport;
    const nowImport = this.totals?.grossImport;
    this.importAvoidedKwh = baseImport !== null && baseImport !== undefined
      && nowImport !== null && nowImport !== undefined
      ? baseImport - nowImport
      : null;

    const days = this.scaledSeriesDays();
    this.savingWindowDays = days;

    // The banded day rate is the same marginal import rate, entered on a newer field. A site with
    // the bands filled in and the old single rate left blank still has a tariff, and dropping the
    // saving line for it reads as "we cannot price this" when we can.
    //
    // The client's own day rate wins once they have saved one: it is the same marginal rate, theirs.
    const clientDay = this.bands?.find(b => b.kind === 'day')?.cent;
    const importCent = clientDay ?? c?.importCentPerKwh ?? c?.importDayCentPerKwh;

    if (this.importAvoidedKwh === null || !importCent || days <= 0) {
      this.annualisedSaving = null;
      return;
    }

    const saved = (this.importAvoidedKwh * Number(importCent)) / 100;

    // Extra export earns the export rate where there is one, and nothing where there is not.
    const baseExport = this.baseTotals?.grossExport ?? 0;
    const nowExport = this.totals?.grossExport ?? 0;
    const exported = Math.max(0, nowExport - baseExport);
    const earned = c?.exportCentPerKwh ? (exported * Number(c.exportCentPerKwh)) / 100 : 0;

    // Scaled to a year by the share of a typical year's sun in these dates, so a summer window and a
    // winter one give the same answer. Only by days where PVGIS has no profile for the site.
    const span = this.scaledSeriesSpan();
    const season = span ? this.seasonFactor(span.start, span.end) : null;
    this.annualisedSaving = season !== null ? (saved + earned) * season : ((saved + earned) / days) * 365;
  }

  /**
   * What to multiply a solar saving over [start, end) by to make it a year's: a typical year's
   * output at the site over the output in those dates, both from PVGIS (SolarYearByDay). Null where
   * there is no profile, or no sun at all in the dates.
   *
   * Jayden, 15/10/26. Counting days (365 over the days on screen) treats every day as equally sunny,
   * so a spring and summer window overstated the year: Lee Strand's +300 kWp said €82k a year on the
   * week view (119 days) and €68k on the month view (154 days) for the same panels. An Irish array
   * makes about two thirds of its year between April and September.
   */
  seasonFactor(start: Date, end: Date): number | null {
    const byDay = this.solarYearByDay;
    if (!byDay || byDay.length !== 366) return null;

    const from = start.getTime(), to = end.getTime();
    if (isNaN(from) || isNaN(to) || to <= from) return null;

    // A normal year: the typical year carries 29 February, which only a leap year has.
    const year = byDay.reduce((a, b) => a + b, 0) - byDay[59];

    let inWindow = 0;
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (day.getTime() < to) {
      const next = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
      // Part days count by the share of the day inside the window.
      const share = (Math.min(to, next.getTime()) - Math.max(from, day.getTime())) / (next.getTime() - day.getTime());
      const doy = Math.round((new Date(2024, day.getMonth(), day.getDate()).getTime() - new Date(2024, 0, 1).getTime()) / 86400000);
      inWindow += byDay[doy] * Math.max(0, share);
      day.setTime(next.getTime());
    }

    return inWindow > 0 && year > 0 ? year / inWindow : null;
  }

  /**
   * The dates the series being scaled was actually running, which is not always the window.
   *
   * Annualising over the whole window understates every euro figure by whatever share of it the
   * array did not exist for. Lee Strand's mains readings run from October 2024 and the array was
   * only commissioned the following April, so a window opened wide enough to catch the 519 kVA
   * peak spreads a year of solar saving across 680 days and quietly knocks a third off the answer.
   *
   * Measured from the first interval the series actually produced anything. Where it produced from
   * the start, or where there is nothing to go on, this is the window and nothing changes.
   */
  private scaledSeriesSpan(): { start: Date; end: Date } | null {
    if (!this.bucketDates.length) return null;

    const full = bucketWindowDays(this.bucketDates);
    const windowStart = this.bucketDates[0].getTime();
    if (isNaN(windowStart)) return null;

    // And only up to the last reading. A month view's last bar is the whole month even when the
    // readings stop on the 4th, and counting the rest of it as days with sun in them shrank the year.
    let end = windowStart + full * 86400000;
    const rows = this.hourlyRows;
    if (rows.length > 1) {
      const lastHour = rows[rows.length - 1].ts.getTime() + (rows[1].ts.getTime() - rows[0].ts.getTime());
      if (lastHour > windowStart && lastHour < end) end = lastHour;
    }
    if (this.newArray) return { start: new Date(windowStart), end: new Date(end) };

    const scaled = this.rawSeries.find(s => s.logicalGroupId === this.scaleGroupId);
    const firstLive = scaled?.points?.find(p => Number(p.value) > 0);
    const live = firstLive?.date ? new Date(firstLive.date).getTime() : NaN;
    const start = !isNaN(live) && live > windowStart && live < end ? live : windowStart;

    return { start: new Date(start), end: new Date(end) };
  }

  /** scaledSeriesSpan in days, at least one. */
  private scaledSeriesDays(): number {
    const span = this.scaledSeriesSpan();
    if (!span) return bucketWindowDays(this.bucketDates);
    return Math.max(1, (span.end.getTime() - span.start.getTime()) / 86400000);
  }

  /**
   * Rebuild the scenario and hand it to the chart.
   *
   * Every control in the template is bound to this. Cheap enough to run on a slider drag: the
   * battery simulation measures about 7 ms over 16,000 hours and the peak shave's 24 halvings about
   * 5 ms on top, both measured rather than assumed.
   */
  applyMultiplier(): void {
    this.recompute();
  }

  private recompute(): void {
    const k = Number(this.multiplier) || 0;
    const scaled = this.scaleSeries(k);
    const drawn = scaled ?? this.rawSeries;

    this.deriveWhatItMeans(k);
    this.runBattery();
    this.runPeak();

    const gridName = this.grid(drawn)?.name ?? null;
    const bands = this.showBatteryBand && gridName ? this.buildBands(gridName, drawn) : [];

    this.scenarioChange.emit({
      series: scaled,
      bands,
      multiplier: k,
      addedKwp: this.estimatedKwp,
      cap: this.effectiveCap,
      note: scaled ? (this.scaleName + ' ' + this.scaleNote).trim() : null,
      basis: this.rawSeries,
      guides: this.guides(scaled !== null)
    });
    this.cdr.markForCheck();
  }

  /**
   * What to mark on the chart, Jayden's ask 24/09/26: both subtle, both answering a question the bars
   * raise on their own.
   *
   * The inverter limit, only once it is actually clipping something in view. Without it the solar
   * bars just stop growing at midday for no visible reason. It is only ever a real ceiling on hourly
   * bars (see effectiveCap), which is also the only view where a flat top can be seen.
   *
   * The night-rate hours, whenever the battery is set to charge from the grid. Charging front-loads
   * into the first hour or two, so without the shading it reads as though the battery only charges
   * at midnight and 01:00, when the cheap rate in fact runs to 08:00.
   */
  private guides(scaled: boolean): ChartGuide[] {
    const guides: ChartGuide[] = [];

    const cap = this.effectiveCap;
    if (scaled && cap !== null && this.clipped > 0) {
      guides.push({ kind: 'level', value: cap, label: `Inverter limit ${Math.round(cap)} kW` });
    }

    // The rate bands, whenever the what-if is open and the site has them, so the hours each price
    // covers can be read straight off the bars. Jayden, 02/10/26: one faint colour for night and a
    // different one for the evening, both quiet enough not to get in the way of the data.
    if (this.hasNightRate) {
      const n = nightHours(this.bands);
      guides.push({ kind: 'hours', fromHour: n.from, toHour: n.to, label: 'Night rate', tone: 'cool' });
    }

    const evening = this.bands?.find(b => b.kind === 'evening');
    if (evening && evening.fromHour != null && evening.toHour != null) {
      guides.push({ kind: 'hours', fromHour: evening.fromHour, toHour: evening.toHour, label: 'Evening rate', tone: 'warm' });
    }

    return guides;
  }

  /**
   * Per-bucket flows for the bands, from a second run of the simulation that keeps its trace.
   *
   * Kept separate from runBattery because only one of the two needs the trace and the trace
   * allocates an object per hour. Nothing else needs paying for that on every slider step.
   */
  private buildBands(gridName: string, series: ResourceSeriesVM[]): SeriesBand[] {
    const flow = this.traceFlowByBucket();
    this.captureByBucket = flow.captured;
    this.dischargeByBucket = flow.discharged;
    this.gridChargeByBucket = flow.gridCharged;
    return this.batteryBands(gridName, series);
  }

  /**
   * Scale the chosen series and let another absorb the difference. Null where nothing changed.
   *
   * Site load is the invariant: scaling generation does not change what the building used, it
   * changes how much of it came off the roof. So the absorbing series is recomputed as load minus
   * generation, which is what lets a big enough multiplier push the grid negative and show export
   * appearing where there was none before.
   */
  private scaleSeries(k: number): ResourceSeriesVM[] | null {
    const cap = this.effectiveCap;
    this.clipped = 0;

    if (this.newArray) return this.addEstimatedArray(k - 1);

    const scaled = this.rawSeries.find(s => (s.logicalGroupId ?? null) === this.scaleGroupId);
    const grid = this.grid(this.rawSeries);
    const absorber = grid !== scaled ? grid : this.rawSeries.find(s => s !== scaled);

    // Nothing to scale, or nowhere for the difference to go: the metered numbers stand untouched.
    if (k === 1 || !scaled || !absorber) return null;

    // Load per interval is unchanged by the multiplier, so take it from the metered figures.
    const loadByDate = new Map<string, number>();
    for (const s of this.rawSeries) {
      for (const p of s.points ?? []) {
        const key = String(p.date);
        loadByDate.set(key, (loadByDate.get(key) ?? 0) + (p.value ?? 0));
      }
    }

    const next: ResourceSeriesVM[] = this.rawSeries.map(s => {
      if (s !== scaled && s !== absorber) return s;

      const points = (s.points ?? []).map(p => {
        if (s === scaled) {
          const raw = k * (p.value ?? 0);
          const value = cap !== null && raw > cap ? cap : raw;
          this.clipped += raw - value;
          return { ...p, value };
        }
        return p;
      });

      return { ...s, points };
    });

    if (absorber !== scaled) {
      const scaledNow = next.find(s => (s.logicalGroupId ?? null) === this.scaleGroupId);
      const absorberIndex = next.findIndex(s => (s.logicalGroupId ?? null) === (absorber.logicalGroupId ?? null));
      const before = new Map<string, number>();
      for (const p of scaled.points ?? []) before.set(String(p.date), p.value ?? 0);
      const byDate = new Map<string, number>();
      for (const p of scaledNow?.points ?? []) byDate.set(String(p.date), p.value ?? 0);

      // The grid takes exactly the change in generation. On a mains and solar pair that is the same
      // as load minus the new generation; on a remainder chart, where the sub meters are part of the
      // load too, it is the only form that leaves them where they were.
      next[absorberIndex] = {
        ...next[absorberIndex],
        points: (next[absorberIndex].points ?? []).map(p => ({
          ...p,
          value: (p.value ?? 0) - ((byDate.get(String(p.date)) ?? 0) - (before.get(String(p.date)) ?? 0))
        }))
      };
    }

    return next;
  }

  /** The estimated array's series as drawn: its own band, in the solar colour, under the mains. */
  static readonly ESTIMATE_NAME = 'Solar PV (estimate)';
  static readonly ESTIMATE_RGB = '#F2B705';

  /**
   * A site with no solar: draw the array being tried as its own series, and take what it generates
   * off the mains bar by bar. Null where nothing is being tried or the estimate has not arrived.
   *
   * Each bar's generation is the hourly estimate summed over the hours the bar covers, so it is the
   * same energy the battery sees, at whatever the chart is showing.
   */
  private addEstimatedArray(kwp: number): ResourceSeriesVM[] | null {
    const grid = this.grid(this.rawSeries);
    if (!(kwp > 0) || !grid || !this.hourlyRows.some(h => h.kwpKwh != null)) return null;

    const points = [...(grid.points ?? [])].filter(p => p.date)
      .sort((a, b) => new Date(a.date as any).getTime() - new Date(b.date as any).getTime());
    const starts = points.map(p => new Date(p.date as any).getTime());
    const end = this.bucketWindowEnd ? this.bucketWindowEnd.getTime() : Number.POSITIVE_INFINITY;
    const perBar = new Array<number>(points.length).fill(0);

    const cap = this.effectiveCap;
    let b = 0;
    for (const h of this.hourlyRows) {
      const ts = h.ts.getTime();
      if (ts >= end) break;
      while (b + 1 < starts.length && starts[b + 1] <= ts) b++;
      if (!starts.length || ts < starts[b]) continue;
      const raw = kwp * (h.kwpKwh ?? 0);
      const value = cap !== null && raw > cap ? cap : raw;
      this.clipped += raw - value;
      perBar[b] += value;
    }

    const genByDate = new Map<string, number>();
    points.forEach((p, i) => genByDate.set(String(p.date), perBar[i]));

    // -1 because it is no logical group, and the chart treats a series without one as a policy and
    // stops reading the stack as grid and generation.
    const estimate: ResourceSeriesVM = {
      logicalGroupId: -1,
      seriesKey: -1,
      name: WhatIfPanelComponent.ESTIMATE_NAME,
      rgb: WhatIfPanelComponent.ESTIMATE_RGB,
      isTotal: false,
      points: points.map((p, i) => ({ date: p.date, label: p.label, value: perBar[i] }))
    };

    return [
      estimate,
      ...this.rawSeries.map(s => s !== grid ? s : {
        ...s,
        points: (s.points ?? []).map(p => ({ ...p, value: (p.value ?? 0) - (genByDate.get(String(p.date)) ?? 0) }))
      })
    ];
  }

  /** Name of the group being scaled, for the note on the tiles. */
  get scaleName(): string {
    return this.scaleOptions.find(o => o.id === this.scaleGroupId)?.name ?? '';
  }
}

/** The kinds of rate a client can add. See dbo.SiteTariffRates for what each one drives. */
export type RateKind = 'day' | 'night' | 'evening' | 'other';

/** One row of the Tariffs fold while it is being edited, the rate in euro per kWh. */
export interface RateRow {
  kind: RateKind;
  name: string;
  eur: number | null;
  fromHour: number | null;
  toHour: number | null;
}

const RATE_NAMES: Record<RateKind, string> = {
  day: 'Day rate',
  night: 'Night rate',
  evening: 'Evening rate',
  other: 'Other rate'
};
